import {
  signUp,
  signIn,
  confirmSignUp,
  autoSignIn,
  signOut,
  getCurrentUser,
  resendSignUpCode,
  fetchAuthSession,
  updatePassword,
  signInWithRedirect,
} from '@aws-amplify/auth'

/**
 * Ripped this out of the types from AWS, they don't expose it properly
 * so I used it here.
 */
export interface AWSCredentials {
  accessKeyId: string
  secretAccessKey: string
  sessionToken?: string
  expiration?: Date
}

/**
 * The AuthStep is a way to track a user's progression through
 * registering with the service. There are other steps that will need
 * to be handled in the future.
 */
export type AuthStep = 'verify' | 'done'

/**
 * Errors to handle from AWS.
 */
type AWSError = 'notAuthorized' | 'alreadySignedIn' | 'notConfirmed' | 'unknown'

/**
 * The needed inputs to sign up with the service.
 */
export interface LoginInput {
  username: string
  password: string
}

/**
 * The needed inputs to sign up with the service.
 */
export interface SignupInput {
  username: string
  password: string
}

export interface UpdatePasswordInput {
  current: string
  update: string
}

/**
 * The needed inputs to verify with the service.
 */
export interface VerifyInput {
  username: string
  code: string
}

export interface AuthenticationService {
  login: (input: LoginInput) => Promise<AuthStep>
  logout: () => Promise<void>
  isSignedIn: () => Promise<boolean>
  signup: (input: SignupInput) => Promise<AuthStep>
  verify: (input: VerifyInput) => Promise<AuthStep>
  getJWT: () => Promise<string | undefined>
  getTemporaryCredentials: () => Promise<AWSCredentials>
  getUser: () => Promise<string>
  resendVerification: (username: string) => Promise<void>
  updatePassword: (input: UpdatePasswordInput) => Promise<void>
  presentGoogleAuth: () => Promise<void>
}

/**
 * The AuthenticationService implementation to interact with AWS authentication modules.
 */
export class AuthService implements AuthenticationService {
  /**
   * Will sign the user via Google IDP
   */
  public async presentGoogleAuth(): Promise<void> {
    await signInWithRedirect({ provider: 'Google' })
  }

  /**
   * Login a user into the service.
   *
   * @param input The inputs needed to login into the service
   * @returns 'done' unless the user has not completed verification.
   */
  public async login(input: LoginInput): Promise<AuthStep> {
    const { password, username } = input
    if (!password && !username) {
      throw new Error('Missing username and/or password')
    }

    const sanitizeUsername = username.toLowerCase()
    try {
      const { nextStep } = await signIn({
        username: sanitizeUsername,
        password,
      })

      if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        return 'verify'
      }
      return 'done'
    } catch (error) {
      const awsError = this.handleAWSError(error)
      if (awsError === 'alreadySignedIn') {
        return 'done'
      }
      console.warn(error)
      throw error
    }
  }

  /**
   * Logs the current user out of the local session.
   */
  public async logout(): Promise<void> {
    try {
      await signOut()
    } catch (error) {
      console.warn(error)
      throw error
    }
  }

  /**
   * Signs the user up with the service.
   *
   * @param input The inputs needed to sign up with the service.
   * @returns 'verify' auth step unless something weird happens.
   */
  public async signup(input: SignupInput): Promise<AuthStep> {
    const { username, password } = input
    try {
      const { nextStep, userId } = await signUp({
        username,
        password,
        options: {
          userAttributes: {},
          autoSignIn: true,
        },
      })

      if (!userId) {
        throw new Error('User created without Id')
      }

      return await this.handleAuthStep(nextStep.signUpStep)
    } catch (error) {
      const awsError = this.handleAWSError(error)
      if (awsError === 'alreadySignedIn') {
        return 'done'
      }
      console.warn(error)
      throw error
    }
  }

  /**
   * Verifies that a user has registered with the service.
   *
   * @param input The user name and the verification code
   * @returns The Auth step to proceed forward
   */
  public async verify(input: VerifyInput): Promise<AuthStep> {
    try {
      const { nextStep } = await confirmSignUp({
        username: input.username,
        confirmationCode: input.code,
      })
      return await this.handleAuthStep(nextStep.signUpStep)
    } catch (error) {
      const awsError = this.handleAWSError(error)
      if (awsError === 'alreadySignedIn') {
        return 'done'
      }
      console.warn(error)
      throw error
    }
  }

  /**
   * Resends the verification code to the user's email.
   *
   * @param username the username to resend the verification code.
   */
  public async resendVerification(username: string): Promise<void> {
    try {
      await resendSignUpCode({ username })
    } catch (error) {
      console.warn(error)
      throw error
    }
  }

  /**
   * Updates the user's password
   *
   * @param input The current password and the password to change to
   */
  public async updatePassword(input: UpdatePasswordInput): Promise<void> {
    const { current, update } = input
    if (current === update) {
      throw new Error('The passwords are the same')
    }
    // TODO: Password validation meets requirements before sending (small optimization i.e. avoid network request)
    try {
      await updatePassword({ oldPassword: current, newPassword: update })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  /**
   * Retrieves the session JWT token.
   *
   * @returns returns the JWT id token.
   */
  public async getJWT(): Promise<string | undefined> {
    return (await fetchAuthSession()).tokens?.idToken?.toString()
  }

  /**
   * Gets the current user
   *
   * @returns the user id
   */
  public async getUser(): Promise<string> {
    try {
      const user = await getCurrentUser()
      return user.userId
    } catch (error) {
      if (
        error instanceof Error &&
        error.name === 'UserUnAuthenticatedException'
      ) {
        throw new Error('Not Signed In')
      }
      throw error
    }
  }

  public async isSignedIn(): Promise<boolean> {
    try {
      return !!(await this.getUser())
    } catch {
      return false
    }
  }

  /**
   * Takes an AWS auth step and either returns the Auth step or finish the auto signin.
   *
   * @param step The AWS auth step
   * @returns
   */
  private async handleAuthStep(
    step: 'DONE' | 'COMPLETE_AUTO_SIGN_IN' | 'CONFIRM_SIGN_UP',
  ): Promise<AuthStep> {
    switch (step) {
      case 'DONE':
        return 'done'
      case 'CONFIRM_SIGN_UP':
        return 'verify'
      case 'COMPLETE_AUTO_SIGN_IN':
        await autoSignIn()
        return 'done'
    }
  }

  /**
   * Overkill handler, but wanted a way to get instance of error
   * and determine what type of error it is.
   *
   * @param error the error to handle.
   * @returns
   */
  private handleAWSError(error: unknown): AWSError {
    if (error instanceof Error) {
      switch (error.name) {
        case 'UserAlreadyAuthenticatedException':
          return 'alreadySignedIn'
        case 'NotAuthorizedException':
          return 'notAuthorized'
        case 'UserNotConfirmedException':
          return 'notConfirmed'
        default:
          console.warn('Unknown error', error)
          return 'unknown'
      }
    }
    console.warn('Unknown error', error)
    return 'unknown'
  }

  public async getTemporaryCredentials() {
    return await this.generateTemporaryCreds()
  }

  // We might not have to do this and can use `await fetchAuthSession().credentials`
  private async generateTemporaryCreds() {
    const session = await fetchAuthSession({ forceRefresh: true })
    const credentials = session.credentials
    if (!credentials) {
      throw new Error('No credentials available')
    }
    return credentials
  }
}
